<template>
  <section class="relative overflow-hidden flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl bg-gray-900/60 px-4 py-3">
    <div v-if="isQueued" class="absolute inset-x-0 bottom-0 h-0.5 bg-primary-950 overflow-hidden" aria-hidden="true">
      <div class="queue-progress h-full w-1/3 bg-primary-500" />
    </div>

    <div class="flex items-center gap-3 flex-1 min-w-0">
      <ul class="flex -space-x-2 flex-shrink-0">
        <li v-for="member in party?.members || []" :key="member.id">
          <img
            v-if="member.avatarfull || member.avatar"
            :src="member.avatarfull || member.avatar"
            :alt="member.username"
            :title="member.username"
            class="w-8 h-8 rounded-full object-cover ring-2 ring-gray-900"
          />
          <img
            v-else
            src="../../assets/logos/refrag-avatar.svg"
            :alt="member.username"
            :title="member.username"
            class="w-8 h-8 rounded-full object-cover ring-2 ring-gray-900"
          />
        </li>
      </ul>
      <div class="min-w-0">
        <p class="flex items-center gap-2 min-w-0">
          <span class="text-sm font-semibold text-text-primary truncate">{{ summaryLabel }}</span>
          <router-link
            v-if="!isQueued && isOwner"
            to="/matchmaking"
            class="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-semibold text-text-secondary hover:text-text-primary hover:bg-gray-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          >
            <PencilSquareIcon class="w-3.5 h-3.5" aria-hidden="true" />
            {{ $t('common.edit') }}
          </router-link>
        </p>
        <p
          v-if="!isQueued"
          class="text-xs truncate"
          :class="statusIsProblem ? 'text-red-400' : 'text-text-secondary'"
          aria-live="polite"
        >{{ statusLabel }}</p>
      </div>
    </div>

    <QueueSearchPill v-if="isQueued || activeLobby" />

    <div v-else class="flex items-center gap-2 flex-shrink-0">
      <button
        v-if="isOwner"
        type="button"
        :disabled="!canBeginMatchmaking || queueing || !!activeLobby"
        @click="beginMatchmaking"
        class="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold shadow-[0_0_20px_rgba(78,84,200,0.3)] hover:bg-primary-500 transition-colors disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
      >
        <ArrowPathIcon class="w-4 h-4" aria-hidden="true" />
        {{ $t('play.matchmaking.requeue') }}
      </button>
    </div>
  </section>
</template>

<script setup>
import { ArrowPathIcon, PencilSquareIcon } from '@heroicons/vue/24/outline';
import QueueSearchPill from './QueueSearchPill.vue';
import { useMatchmakingQueue } from '../../composables/useMatchmakingQueue';

const {
  party,
  activeLobby,
  isQueued,
  isOwner,
  queueing,
  canBeginMatchmaking,
  summaryLabel,
  statusIsProblem,
  statusLabel,
  beginMatchmaking,
} = useMatchmakingQueue();
</script>

<style scoped>
@keyframes queue-progress {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(300%); }
}

.queue-progress {
  animation: queue-progress 1.8s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .queue-progress {
    animation: none;
    width: 100%;
    opacity: 0.6;
  }
}
</style>
